import {
    AdState,
    AllEvents,
    ArticlePublication,
    ComponentServices,
    GalleryPublication,
    mapPublication,
} from '@news-mono/web-common'
import H from 'history'
import { Logger } from 'typescript-log'
import { AdUnitInformation } from '../../advertising/advertising.routing'
import { ImageLinkProps } from '../../buttons/ImageLink'
import { ImageLinkVisibilityMode } from '../../buttons/ImageLink/ImageLink.styled'
import { Section } from '../../__styling/settings/sections'

export interface InlinePublicationContent {
    kind: string
    /**
     * Insert ad after n content blocks, falls back to the PerthNow default of 4 if not
     * specified.
     */
    insertAfter?: number
}

export interface InlineAdContent extends InlinePublicationContent {
    kind: 'ad'
    /**
     * Sometimes we need to insert multiple bits of content into the same position, due to different properties or breakpoints
     */
    content: AdUnitInformation['data'][]
}

export interface InlineOovvuuFeaturedCardContent
    extends InlinePublicationContent {
    kind: 'oovvuu-featured-card'
    props: {
        oovvuuUrl: string
        oovvuuPath: string
    }
}

export interface InlinePointer extends InlinePublicationContent {
    kind: 'pointer'
    props: {
        pointerLinkUrl: string
        pointerImageSrc: string
        pointerImageAlt: string
    }
}

export interface InlineSportCallToAction extends InlinePublicationContent {
    kind: 'sport-call-to-action'
}

export interface InlineSportsHubCallToAction extends InlinePublicationContent {
    kind: 'sports-hub-call-to-action'
}

interface InlineImageLinkProps extends ImageLinkProps {
    visiblility: ImageLinkVisibilityMode
    displayCondition?: boolean
}

export interface InlineImageLink extends InlinePublicationContent {
    kind: 'image-link'
    props: InlineImageLinkProps
}

export interface InlineCngNewsletterBanner extends InlinePublicationContent {
    kind: 'inline-cng-newsletter-banner'
    props: InlineImageLinkProps
}

export interface InlineWestLiveBanner extends InlinePublicationContent {
    kind: 'the-west-live-in-article-banner'
}

export interface InlineTaboolaAmpComponent extends InlinePublicationContent {
    kind: 'inline-taboola-amp'
    insertAfter: number
}
export interface InlineTaboolaComponent extends InlinePublicationContent {
    kind: 'inline-taboola'
    insertAfter: number
}

export interface InlineArticleContextualLink extends InlinePublicationContent {
    kind: 'inline-article-contextual-link'
}

export type InlineContentTypes =
    | InlineAdContent
    | InlineOovvuuFeaturedCardContent
    | InlinePointer
    | InlineSportCallToAction
    | InlineImageLink
    | InlineWestLiveBanner
    | InlineCngNewsletterBanner
    | InlineSportsHubCallToAction
    | InlineTaboolaAmpComponent
    | InlineTaboolaComponent
    | InlineArticleContextualLink

export interface GalleryPublicationInformation {
    kind: 'gallery'
    slug: string
    /**
     * If we don't have route hints we will load the full publication when resolving
     * the route. When we have route hints we won't need to so the ContentAreaDataLoader
     * will load the publication
     */
    numberImages: number
    /* type: ad-unit, and data: AdUnitProps */
    galleryAds: AdUnitInformation['data'][]
}

export interface RemainingPublicationInformation {
    kind: 'article'
    slug: string
    inlinePublicationContent?: InlineContentTypes[]
    /**
     * If we don't have route hints we will load the full publication when resolving
     * the route. When we have route hints we won't need to so the ContentAreaDataLoader
     * will load the publication
     */
}

export interface PublicationRouteProps {
    meta: GalleryPublicationInformation | RemainingPublicationInformation
    section: Section
    isVideoStickyEnabled?: boolean
}

export interface GalleryPublicationProps {
    gallery: GalleryPublication
    galleryMeta: GalleryPublicationInformation
    onEvent: (event: AllEvents) => void
    location: H.Location
    adState: AdState
    log: Logger
}

export function getPublicationInfoFromRouteResolution(
    services: ComponentServices,
) {
    if (services.resolution.type !== 'server') {
        throw new Error('Publications must be server resolved')
    }

    if (
        services.resolution.resolution.type !== 'publication' &&
        services.resolution.resolution.type !== 'preview'
    ) {
        throw new Error(
            'Server resolution is not publication, should not be rendering publication',
        )
    }

    const publication = mapPublication(
        services.resolution.resolution.publication,
        services.store.getState().toggles,
    )
    return {
        publication,
        section: services.resolution.resolution.section as Section,
    }
}

export function getVideoSeriesEpisodeInfoFromRouteResolution(
    services: ComponentServices,
) {
    if (services.resolution.type !== 'server') {
        throw new Error('Publications must be server resolved')
    }

    if (services.resolution.resolution.type !== 'video-series-episode') {
        throw new Error(
            'Server resolution is not publication, should not be rendering publication',
        )
    }

    return services.resolution.resolution
}

export type PublicationTypes =
    | ArticlePublication
    | GalleryPublication
    | undefined
export type MetaTypes =
    | GalleryPublicationInformation
    | RemainingPublicationInformation
